import {
  ImportParseResult,
  ImportRow,
  detectType,
  makeImportId,
  normalizeDate,
  parseAmount,
} from './types';

function splitCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
}

function headerIndex(headers: string[], aliases: string[]): number {
  const normalized = headers.map((h) => h.toLowerCase().replace(/[\s_]/g, ''));
  for (const alias of aliases) {
    const key = alias.toLowerCase().replace(/[\s_]/g, '');
    const idx = normalized.indexOf(key);
    if (idx >= 0) return idx;
  }
  return -1;
}

export function parseCsvText(text: string): ImportParseResult {
  const lines = text
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  const warnings: string[] = [];
  if (lines.length < 2) {
    return { rows: [], source: 'csv', warnings: ['CSV file is empty or missing a header row.'] };
  }

  const headers = splitCsvLine(lines[0]);
  const dateIdx = headerIndex(headers, ['date', 'trn.date', 'transactiondate', 'trn date']);
  const typeIdx = headerIndex(headers, ['type', 'transactiontype', 'drcr']);
  const amountIdx = headerIndex(headers, ['amount', 'amt']);
  const debitIdx = headerIndex(headers, ['debit', 'debits', 'withdrawal', 'out']);
  const creditIdx = headerIndex(headers, ['credit', 'credits', 'deposit', 'in']);
  const descIdx = headerIndex(headers, [
    'description',
    'descriptions',
    'narration',
    'particulars',
    'details',
    'memo',
  ]);

  if (dateIdx < 0 || (amountIdx < 0 && debitIdx < 0 && creditIdx < 0)) {
    return {
      rows: [],
      source: 'csv',
      warnings: [
        'Could not find Date and Amount (or Debit/Credit) columns. Use headers like Date, Type, Amount, Description.',
      ],
    };
  }

  const rows: ImportRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = splitCsvLine(lines[i]);
    const date = normalizeDate(cols[dateIdx] || '');
    if (!date) {
      warnings.push(`Skipped row ${i + 1}: invalid date.`);
      continue;
    }

    let type = detectType(typeIdx >= 0 ? cols[typeIdx] : '');
    let amount: number | null = null;

    if (debitIdx >= 0 || creditIdx >= 0) {
      const debit = debitIdx >= 0 ? parseAmount(cols[debitIdx] || '') : null;
      const credit = creditIdx >= 0 ? parseAmount(cols[creditIdx] || '') : null;
      if (credit && credit > 0) {
        type = 'income';
        amount = credit;
      } else if (debit && debit > 0) {
        type = 'expense';
        amount = debit;
      }
    } else {
      amount = parseAmount(cols[amountIdx] || '');
      if (typeIdx < 0 && String(cols[amountIdx] || '').trim().startsWith('-')) {
        type = 'expense';
      }
    }

    if (!amount || amount <= 0) {
      warnings.push(`Skipped row ${i + 1}: missing amount.`);
      continue;
    }

    rows.push({
      id: makeImportId(i),
      date,
      type,
      amount,
      description: (descIdx >= 0 ? cols[descIdx] : '') || 'Imported',
      marked: true,
    });
  }

  return { rows, source: 'csv', warnings };
}

export async function parseCsvFile(file: File): Promise<ImportParseResult> {
  const text = await file.text();
  return parseCsvText(text);
}
